import { ReactNode } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import { createBEMClasses } from '@/utils/classname';

import './Stepper.css';

const { block, element } = createBEMClasses('stepper');

export interface StepProps {
  name: string;
  label?: ReactNode;
  status: 'done' | 'in-progress' | 'pending' | 'cancelled' | 'issue';
}

export interface StepperProps {
  variant?: 'linear' | 'round';
  size?: 'x-small' | 'small' | 'medium' | 'large';
  steps: StepProps[];
  showStepCount?: boolean;
  labelVariant?: 'awaiting' | 'pending' | 'done';
  children?: ReactNode;
}

export const Statuses = {
  done: msg({ id: 'stepper.status.done', message: 'Done' }),
  'in-progress': msg({ id: 'stepper.status.in-progress', message: 'In progress' }),
  pending: msg({ id: 'stepper.status.pending', message: 'Pending' }),
  cancelled: msg({ id: 'stepper.status.cancelled', message: 'Cancelled' }),
  issue: msg({ id: 'stepper.status.issue', message: 'Issue' }),
};

export const Stepper = ({
  variant = 'linear',
  size = 'small',
  steps,
  showStepCount = false,
  labelVariant,
  children,
}: StepperProps) => {
  const { _ } = useLingui();

  const { currentStepIndex, totalStepsCount } = getCurrentAndTotalStep(steps);

  const stepDegrees = 360 / totalStepsCount;
  const gapDegrees = 15;

  return (
    <div
      className={block({
        variant,
        size,
        'show-step-count': showStepCount,
      })}
    >
      <div className={element('header')}>
        <span
          className={element('header__label', {
            'has-variant': !!labelVariant,
            variant: labelVariant,
          })}
        >
          <span className={element('header__label__step-count')}>
            <Trans id="stepper.step">
              Step {currentStepIndex + 1}/{totalStepsCount}:
            </Trans>
          </span>
          <span className={element('header__label__text')}>
            {steps[currentStepIndex].label ?? steps[currentStepIndex].name}
          </span>
        </span>
        <span className={element('header_children')}>{children}</span>
      </div>
      {variant === 'linear' && (
        <ol className={element('steps')}>
          {steps.map((step, index) => (
            <li
              key={index}
              className={element('step', {
                status: step.status,
                current: index === currentStepIndex,
              })}
              aria-label={_(
                msg({
                  id: 'stepper.step.label',
                  message: `Step ${step.name}: ${_(Statuses[step.status])}`,
                })
              )}
              aria-current={index === currentStepIndex ? 'step' : false}
            >
              <div className={element('step__indicator')}>
                <div className={element('step__indicator__first')} />
                <div className={element('step__indicator__second')} />
              </div>
              <span className={element('step__label')}>{step.label ?? step.name}</span>
            </li>
          ))}
        </ol>
      )}
      {variant === 'round' && (
        <svg
          className={element('steps')}
          viewBox="0 0 100 100"
          style={{
            strokeDasharray: `${stepDegrees - gapDegrees} ${360 - stepDegrees + gapDegrees}`,
          }}
        >
          {steps.map((step, index) => (
            <circle
              key={index}
              className={element('step', { status: step.status })}
              cx="50"
              cy="50"
              r="42.5"
              transform={`rotate(${stepDegrees * index} 50 50)`}
              pathLength={360}
              aria-label={_(
                msg({
                  id: 'stepper.step.label',
                  message: `Step ${step.name}: ${_(Statuses[step.status])}`,
                })
              )}
            />
          ))}
        </svg>
      )}
    </div>
  );
};

export const StepperTooltip = ({
  steps,
  children,
}: {
  steps: StepProps[];
  children?: ReactNode;
}) => {
  const { currentStepIndex, totalStepsCount } = getCurrentAndTotalStep(steps);

  return (
    <Tooltip
      content={
        <p>
          <InlineText size="400" weight="medium" color="disabled">
            <Trans id="stepper.step">
              Step {currentStepIndex + 1}/{totalStepsCount}:
            </Trans>
          </InlineText>
          <br />
          <InlineText size="200" color="disabled">
            {steps[currentStepIndex].name}
          </InlineText>
        </p>
      }
    >
      <Button variant="style-less">{children}</Button>
    </Tooltip>
  );
};

export function getCurrentAndTotalStep(steps: StepProps[]) {
  /**
   * The current step is the first step 'in-progress', or the first step 'pending', or the last step.
   */
  let currentStepIndex = steps.findIndex((step) => step.status === 'in-progress');

  if (currentStepIndex < 0) {
    currentStepIndex = steps.findIndex((step) => step.status === 'pending');
  }
  if (currentStepIndex < 0) {
    currentStepIndex = steps.length - 1;
  }
  const totalStepsCount = steps.length;

  return { currentStepIndex, totalStepsCount };
}
